import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DEBUG_ENABLED } from '../../utils/DebugLogger';
import './LayerControls.css';

/**
 * LayerControls Component - Professional Layer Management System
 * 
 * This component provides a Roll20-style layer management system for organizing
 * different types of assets on the battle map. It's essential for keeping maps,
 * characters, and GM-only content properly separated and controlled.
 * 
 * THE THREE LAYER SYSTEM:
 * - Map Layer (🗺️): Background maps, terrain, static elements
 * - Token Layer (👤): Player characters, monsters, NPCs that participate in combat
 * - GameMaster Layer (🎲): Hidden GM notes, secret doors, traps, etc.
 * 
 * WHAT IT CONTROLS:
 * - Active Layer: Which layer new assets get placed on when uploaded
 * - Visibility: Show/hide entire layers (👁️ = visible, 🚫 = hidden)
 * - Lock Status: Prevent accidental editing (🔒 = locked, 🔓 = unlocked)
 * - Layer Descriptions: Tooltips explaining what each layer is for
 * 
 * HOW IT WORKS:
 * - Compact dropdown interface that expands to show all layer options
 * - Click layer name to make it active (where new assets will be placed)
 * - Click eye icon to toggle visibility of that layer
 * - Click lock icon to prevent editing assets on that layer
 * - Active layer is highlighted with blue background
 * 
 * INTEGRATION:
 * - Connected to BattleRoom's layer state management
 * - Controls which assets can be selected and moved
 * - Determines where newly uploaded assets are placed
 * - Works with context menu for moving assets between layers
 */

const LayerControls = ({ 
  layers, 
  activeLayer, 
  onActiveLayerChange, 
  onLayerVisibilityChange,
  onLayerLockChange,
  showIndicators = true,
  hideCompactLabel = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const defaultLayers = {
    map: {
      id: 'map',
      name: 'Map Layer',
      icon: '🗺️',
      visible: true,
      locked: false,
      description: 'Static backgrounds and maps'
    },
    token: {
      id: 'token', 
      name: 'Token Layer',
      icon: '👤',
      visible: true,
      locked: false,
      description: 'Characters, monsters, and objects'
    },
    gamemaster: {
      id: 'gamemaster',
      name: 'GameMaster Layer', 
      icon: '🎲',
      visible: true,
      locked: false,
      description: 'GM-only assets and preparations'
    }
  };

  const currentLayers = { ...defaultLayers, ...layers };
  const resolvedActiveLayer = currentLayers[activeLayer] ? activeLayer : 'token';

  const handleActiveLayerSelect = (layerId, event = null) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (onActiveLayerChange) {
      onActiveLayerChange(layerId);
    }
    setIsExpanded(false);
  };

  const toggleLayerVisibility = (layerId, e) => {
    e.stopPropagation();
    if (onLayerVisibilityChange) {
      onLayerVisibilityChange(layerId, !currentLayers[layerId].visible);
    }
  };

  const toggleLayerLock = (layerId, e) => {
    e.stopPropagation();
    if (onLayerLockChange) {
      onLayerLockChange(layerId, !currentLayers[layerId].locked);
    }
  };

  const getActiveLayerInfo = () => {
    const layer = currentLayers[resolvedActiveLayer];
    return layer || currentLayers.token;
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideTrigger = containerRef.current?.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const toggleDropdown = (e) => {
    if (!isExpanded) {
      // Calculate position for fixed dropdown
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width
      });
    }
    setIsExpanded(!isExpanded);
  };

  if (!showIndicators) {
    return (
      <div className={`layer-controls compact-mode ${hideCompactLabel ? 'label-hidden' : ''}`.trim()}>
        <div className="layer-controls-header compact-header">
          {!hideCompactLabel && <span className="controls-label">Layers</span>}
          <label className="compact-layer-selector" htmlFor="active-layer-select">
            <span className="layer-icon" aria-hidden="true">{getActiveLayerInfo().icon}</span>
            <select
              id="active-layer-select"
              className="compact-layer-select"
              aria-label="Active layer"
              value={resolvedActiveLayer}
              onChange={(event) => handleActiveLayerSelect(event.target.value, event)}
            >
              {Object.values(currentLayers).map(layer => (
                <option key={layer.id} value={layer.id}>
                  {layer.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="layer-controls" ref={containerRef}>
      <div className="layer-controls-header">
        <span className="controls-label">Layers</span>
        <div 
          className={`active-layer-selector ${isExpanded ? 'expanded' : ''}`}
          onClick={toggleDropdown}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleDropdown(event);
            }
          }}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={isExpanded}
        >
          <span className="active-layer-display">
            <span className="layer-icon">{getActiveLayerInfo().icon}</span>
            <span className="layer-name">{getActiveLayerInfo().name}</span>
          </span>
          <span className="dropdown-arrow">▼</span>
        </div>
      </div>

      {isExpanded && typeof document !== 'undefined' && createPortal(
        <div 
          ref={dropdownRef}
          className="layer-dropdown"
          role="listbox"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
        >
          {Object.values(currentLayers).map(layer => (
            <div 
              key={layer.id}
              className={`layer-item ${activeLayer === layer.id ? 'active' : ''}`}
              onMouseDown={(event) => handleActiveLayerSelect(layer.id, event)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleActiveLayerSelect(layer.id, event);
                }
              }}
              role="option"
              tabIndex={0}
              aria-selected={activeLayer === layer.id}
            >
              <div className="layer-info">
                <span className="layer-icon">{layer.icon}</span>
                <div className="layer-details">
                  <span className="layer-name">{layer.name}</span>
                  <span className="layer-description">{layer.description}</span>
                </div>
              </div>
              
              <div className="layer-controls-buttons">
                <button
                  className={`control-btn visibility-btn ${layer.visible ? 'active' : ''}`}
                  onClick={(e) => toggleLayerVisibility(layer.id, e)}
                  title={layer.visible ? 'Hide layer' : 'Show layer'}
                >
                  {layer.visible ? '👁️' : '🚫'}
                </button>
                
                <button
                  className={`control-btn lock-btn ${layer.locked ? 'active' : ''}`}
                  onClick={(e) => toggleLayerLock(layer.id, e)}
                  title={layer.locked ? 'Unlock layer' : 'Lock layer'}
                >
                  {layer.locked ? '🔒' : '🔓'}
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}

      {showIndicators && (
        <div className="layer-status">
          {Object.values(currentLayers).map(layer => (
            <div 
              key={layer.id}
              className={`layer-indicator ${layer.visible ? 'visible' : 'hidden'} ${layer.locked ? 'locked' : 'unlocked'}`}
              title={`${layer.name}: ${layer.visible ? 'Visible' : 'Hidden'}, ${layer.locked ? 'Locked' : 'Unlocked'}`}
            >
              <span className="indicator-icon">{layer.icon}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Check active layer change
  if (prevProps.activeLayer !== nextProps.activeLayer) return false;
  
  // Deep compare layers objects
  const prevLayers = prevProps.layers || {};
  const nextLayers = nextProps.layers || {};
  
  // Get all layer IDs from both objects
  const allLayerIds = [...new Set([
    ...Object.keys(prevLayers),
    ...Object.keys(nextLayers)
  ])];
  
  // Check if any layer has changed
  for (const id of allLayerIds) {
    const prevLayer = prevLayers[id] || {};
    const nextLayer = nextLayers[id] || {};
    
    // Check properties that would affect rendering
    if (prevLayer.visible !== nextLayer.visible) return false;
    if (prevLayer.locked !== nextLayer.locked) return false;
    if (prevLayer.name !== nextLayer.name) return false;
    if (prevLayer.icon !== nextLayer.icon) return false;
  }
  
  // Check if handler functions have changed (reference equality)
  if (prevProps.onActiveLayerChange !== nextProps.onActiveLayerChange) return false;
  if (prevProps.onLayerVisibilityChange !== nextProps.onLayerVisibilityChange) return false;
  if (prevProps.onLayerLockChange !== nextProps.onLayerLockChange) return false;
  
  // If we got here, the props that matter haven't changed
  if (DEBUG_ENABLED) console.debug('[LayerControls] Skipping re-render - props are equivalent');
  return true;
};

export default React.memo(LayerControls, arePropsEqual);
